import {useState} from "react";
import { useEffect, useMemo, useCallback } from "react";
import TaskCard from "./TaskCard";
import TaskInput from "./TaskInput";
import { collection, addDoc, doc, deleteDoc, updateDoc, onSnapshot, serverTimestamp } from "firebase/firestore";
import { db } from "./services/firebase";
import { auth } from "./services/firebase";
import { onAuthStateChanged, getRedirectResult, setPersistence, browserLocalPersistence } from "firebase/auth";
import { signInWithGoogle, logout } from "./services/auth";
import { SpeedInsights } from "@vercel/speed-insights/react"

const priorityOrder = {
  high: 3,
  medium: 2,
  low: 1
};

function App() {
  // Initalize variables
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [tasks, setTasks] = useState([]);
  const [priority, setPriority] = useState("medium");
  const [dueDate, setDueDate] = useState("");
  const [activeView, setActiveView] = useState(() => {
    return localStorage.getItem("activeView") || "all";
  });
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [editForm, setEditForm] = useState({
    title: "",
    description: "",
    priority: "medium",
    dueDate: ""
  });

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    let unsubscribe;

    const setupAuth = async () => {
      try {
        await setPersistence(auth, browserLocalPersistence);

        unsubscribe = onAuthStateChanged(auth, (currentUser) => {
          setUser(currentUser);
          setLoading(false);
        });

        await getRedirectResult(auth);

      } catch {
        setLoading(false);
      }
    };

    setupAuth();

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!user) {
      setTasks([]);
    }
  }, [user]);

  useEffect(() => {
    if (!user) return;

    const unsubscribe = onSnapshot(
      collection(db, "users", user.uid, "tasks"),
      (snapshot) => {
        const tasksFromDb = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        setTasks(tasksFromDb);
      }
    );

    return () => unsubscribe();
  }, [user]);

  useEffect(() => {
  if (!user || tasks.length === 0) return;

  const deleteOldCompletedTasks = async () => {
    const now = Date.now();

    const oldCompletedTasks = tasks.filter(task => {
      if (!task.completed || !task.completedAt) return false;

      const completedTime = task.completedAt.seconds * 1000;

      return now - completedTime >= 24 * 60 * 60 * 1000;
    });

    try {
      await Promise.all(
        oldCompletedTasks.map(task =>
          deleteDoc(doc(db, "users", user.uid, "tasks", task.id))
        )
      );
    } catch (error) {
      console.error("Error auto deleting tasks:", error);
    }
  };

  deleteOldCompletedTasks();
}, [tasks, user]);

  useEffect(() => {
    localStorage.setItem("activeView", activeView);
  }, [activeView]);

  // Function for adding task
  const addTask = useCallback(async () => {
    if (!title.trim() || !user) return; // Validate

    const newTask = {
      title,
      description,
      priority,
      dueDate: dueDate || null,
      completed: false,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    // Add to database
    try {
      await addDoc(collection(db, "users", user.uid, "tasks"), newTask);

      // Clear input fields
      setTitle("");
      setDescription("");
      setPriority("medium");
      setDueDate("");

    } catch (error) {
      console.error("Error adding task: ", error)
    }
  }, [title, description, priority, dueDate, user]);

  const toggleComplete = useCallback(async (id, currentValue) => {
    try {
      await updateDoc(doc(db, "users", user.uid, "tasks", id), {
        completed: !currentValue,
        completedAt: !currentValue ? serverTimestamp() : null,
        updatedAt: serverTimestamp()
      });

    } catch (error) {
      console.error("Error toggling task: ", error);
    }
  }, [user]);

  const deleteTask = useCallback(async (id) => {
    // Delete from database
    try {
      await deleteDoc(doc(db, "users", user.uid, "tasks", id));
    } catch (error) {
      console.error("Error deleting task: ", error)
    }
  }, [user])

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") {
        setSearchQuery("");
      }
    };

    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, []);

  const deleteCompletedTasks = useCallback(async () => {
    // Delete all completed tasks
    try {
      const completedTasks = tasks.filter(task => task.completed);

      await Promise.all(
        completedTasks.map(task => 
          deleteDoc(doc(db, "users", user.uid, "tasks", task.id))
        )
      );

    } catch (error) {
      console.error("Error deleting all completed tasks: ", error);
    }

  },[tasks, user]);

  const filteredTasks = useMemo(() => {
    const sorted = [...tasks];

    sorted.sort((a, b) => {
      if (a.completed !== b.completed) {
        return a.completed - b.completed;
      }

      if (a.dueDate && b.dueDate && a.dueDate !== b.dueDate) {
        return a.dueDate.localeCompare(b.dueDate);
      }

      if (a.priority !== b.priority) {
        return priorityOrder[b.priority] - priorityOrder[a.priority];
      }

      if (a.createdAt && b.createdAt) {
        return a.createdAt.seconds - b.createdAt.seconds;
      }

      return 0;
    });

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayNum = today.getTime();

    const getDayNumber = (dateStr) => {
      const d = new Date(dateStr + "T00:00:00");
      d.setHours(0, 0, 0, 0);
      return d.getTime();
    };

    return sorted.filter(task => {
      const query = searchQuery.trim().toLowerCase();

      const matchesSearch =
        task.title.toLowerCase().includes(query) ||
        (task.description || "").toLowerCase().includes(query);

      if (!matchesSearch) return false;

      if (activeView === "completed") return task.completed;

      if (activeView === "today") {
        if (!task.dueDate) return false;
        return getDayNumber(task.dueDate) === todayNum;
      }

      if (activeView === "overdue") {
        if (!task.dueDate) return false;
        return getDayNumber(task.dueDate) < todayNum;
      }

      return true;
    });

  }, [tasks, searchQuery, activeView]);

  // Priority coloring
  const getPriorityColor = (priority) => {
    if (priority === "high") return "#ff0000";
    if (priority === "medium") return "#ffbb00";
    if (priority === "low") return "#00962d";
  }

  const isOverdue = (dueDate) => {
  if (!dueDate) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const taskDate = new Date(dueDate + "T00:00:00");
  taskDate.setHours(0, 0, 0, 0);

  return taskDate < today;
  };

  // Progress dashboard

  const stats = useMemo(() => {
    const totalTasks = tasks.length;

    const completedTasks = tasks.filter(
      task => task.completed
    ).length;

    const progressPercent = totalTasks === 0
      ? 0
      : Math.round((completedTasks / totalTasks) * 100);

    return {
      totalTasks,
      completedTasks,
      progressPercent
    };
  }, [tasks]);

  // Edit tasks
  const startEdit = useCallback((task) => {
    setEditingTaskId(task.id);
    setEditForm({
      title: task.title,
      description: task.description,
      priority: task.priority,
      dueDate: task.dueDate
    });
  }, []);

  const saveEdit = useCallback(async (id) => {
    if (!editForm.title.trim()) return;
    
    try {
      await updateDoc(doc(db, "users", user.uid, "tasks", id), {
        title: editForm.title,
        description: editForm.description,
        priority: editForm.priority,
        dueDate: editForm.dueDate || null,
        updatedAt: serverTimestamp()
      })

    } catch (error) {
      console.error("Error editing task: ", error);
    }

    setEditingTaskId(null);
  }, [editForm, user]);

  const cancelEdit = useCallback(() => {
    setEditingTaskId(null);
  }, []);

  if (loading) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <h2>Loading...</h2>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <h1 className="auth-title">Smart Task Manager</h1>
          <p className="auth-subtitle">
            Organize your tasks and stay focused. Be smart: use Task Manager <br></br><br></br>
            Sign in now to continue
          </p>

          <button onClick={signInWithGoogle} className="google-btn">
            <img
              src="https://developers.google.com/identity/images/g-logo.png"
              alt="Google"
              className="google-icon"
            />
            <span>Continue with Google</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>

      <div className="auth-container">
        <span style={{ fontSize: "17px" }} >Welcome, {user.displayName}</span>
        <button
          onClick={logout}
          className="button delete"
          style={{ marginLeft: "10px", fontSize: "15px"}}
        >
          Logout
        </button>
      </div>

      <div className = "container">
        <h1>Smart Task Manager</h1>

        <div className="stats-bar">
          <div className="stats-left">
            Tasks: {stats.totalTasks}
          </div>

          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{ width: `${stats.progressPercent}%` }}
            />
          </div>

          <div className="stats-right">
            <div className="progress-text">
              {stats.completedTasks} / {stats.totalTasks} ({stats.progressPercent}%)
            </div>
          </div>
        </div>

        <div className="view-bar">
          {["all", "completed", "today", "overdue"].map((view) => (
            <button
              key={view}
              onClick={() => setActiveView(view)}
              className={`view-tab ${activeView === view ? "active" : ""}`}
            >
              {view === "all" && "All"}
              {view === "completed" && "Completed"}
              {view === "today" && "Due Today"}
              {view === "overdue" && "Overdue"}
            </button>
          ))}
        </div>

        <TaskInput
          title={title}
          description={description}
          priority={priority}
          dueDate={dueDate}
          addTask={addTask}
          setTitle={setTitle}
          setDescription={setDescription}
          setPriority={setPriority}
          setDueDate={setDueDate}
        />

        <div className="search-container container-inner">
          <input
            type="text"
            placeholder="Search tasks..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />

          {searchQuery && (
            <button
              className="search-clear"
              onClick={() => setSearchQuery("")}
            >
              ✕
            </button>
          )}
        </div>

        {tasks.some(task => task.completed) && activeView === "completed" && (
          <button
            onClick={deleteCompletedTasks}
            className="button delete"
            style={{ marginBottom: "15px" }}
          >
            Delete All Completed Tasks
          </button>
        )}

        <div>
          {filteredTasks.length === 0 ? (
            <div className="empty-state">
              <h3>No tasks found</h3>

              {searchQuery ? (
                <p>Try changing your search.</p>
              ) : activeView === "completed" ? (
                <p>No completed tasks yet.</p>
              ) : activeView === "today" ? (
                <p>No tasks due today.</p>
              ) : activeView === "overdue" ? (
                <p>No overdue tasks.</p>
              ) : (
                <p>Add your first task to get started.</p>
              )}
            </div>
          ) : (
            filteredTasks.map((task) => (
              <TaskCard
                key={task.id}
                task={task}
                editingTaskId={editingTaskId}
                editForm={editForm}
                setEditForm={setEditForm}
                startEdit={startEdit}
                saveEdit={saveEdit}
                cancelEdit={cancelEdit}
                toggleComplete={toggleComplete}
                deleteTask={deleteTask}
                isOverdue={isOverdue}
                getPriorityColor={getPriorityColor}
              />
            ))
          )}
        </div>
      </div>
      <SpeedInsights />
    </div>
  );
}

export default App;